import { useCallback, useEffect, useRef, useState } from "react";
import { ApiRequestError } from "../api/client.js";
import { ChunkUploader, downloadToDisk, type UploadProgress } from "../api/transfer.js";
import {
  UPLOAD_STATUS_TEXT,
  vaultApi,
  vaultDownloadUrl,
  vaultUploadBackend,
  type VaultEntry,
  type VaultProvider,
  type VaultUpload,
} from "../api/vault.js";
import { Button, Icons, Progress, Status, useToast } from "../design/components/index.js";
import { formatBytes } from "./TransferPanel.js";

/** Ein laufender Upload samt lokalem Fortschritt. */
interface ActiveUpload {
  key: string;
  name: string;
  size: number;
  /** Fortschritt der Übertragung zum Server. */
  local: UploadProgress | null;
  /** Zustand der Weiterverarbeitung, kommt vom Server. */
  server: VaultUpload | null;
  // `| undefined` ist unter exactOptionalPropertyTypes noetig, damit sich ein
  // alter Fehler ausdruecklich zuruecksetzen laesst.
  error?: string | undefined;
}

/**
 * Datei-Browser des geschuetzten Bereichs.
 *
 * Die Oberflaeche richtet sich nach den gemeldeten Faehigkeiten der
 * Datenquelle, statt Sonderfaelle je Provider einzubauen. Paperless wird
 * spaeter keine Ordner melden -- dann verschwinden Navigation und
 * Ordner-anlegen von selbst.
 */
export function VaultBrowser(): React.ReactElement {
  const toast = useToast();

  const [providers, setProviders] = useState<VaultProvider[]>([]);
  const [activeProvider, setActiveProvider] = useState<VaultProvider | null>(null);

  const [folderId, setFolderId] = useState("");
  const [parentId, setParentId] = useState<string | null>(null);
  const [entries, setEntries] = useState<VaultEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [truncated, setTruncated] = useState(false);

  const [uploads, setUploads] = useState<ActiveUpload[]>([]);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);

  // --- Laden -----------------------------------------------------------------

  useEffect(() => {
    void (async () => {
      try {
        const data = await vaultApi.providers();
        setProviders(data.providers);
        setActiveProvider(data.providers[0] ?? null);
      } catch (caught) {
        setError(
          caught instanceof ApiRequestError ? caught.message : "Die Datenquellen konnten nicht geladen werden.",
        );
        setLoading(false);
      }
    })();
  }, []);

  const loadFolder = useCallback(
    async (provider: VaultProvider, target: string) => {
      setLoading(true);
      setError(null);
      setSearching(false);
      setTruncated(false);
      try {
        const result = await vaultApi.list(provider.id, target);
        setFolderId(result.folderId);
        setParentId(result.parentId);
        setEntries(result.entries);
      } catch (caught) {
        setError(caught instanceof ApiRequestError ? caught.message : "Der Ordner ließ sich nicht öffnen.");
        setEntries([]);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    if (activeProvider) void loadFolder(activeProvider, "");
  }, [activeProvider, loadFolder]);

  const refresh = useCallback(() => {
    if (activeProvider && !searching) void loadFolder(activeProvider, folderId);
  }, [activeProvider, folderId, loadFolder, searching]);

  // --- Suche -----------------------------------------------------------------

  const runSearch = useCallback(async () => {
    if (!activeProvider || query.trim().length === 0) return;
    setLoading(true);
    setError(null);
    try {
      const result = await vaultApi.search(activeProvider.id, query.trim());
      setEntries(result.entries);
      setTruncated(result.truncated);
      setSearching(true);
    } catch (caught) {
      setError(caught instanceof ApiRequestError ? caught.message : "Die Suche ist fehlgeschlagen.");
    } finally {
      setLoading(false);
    }
  }, [activeProvider, query]);

  // --- Herunterladen ---------------------------------------------------------

  const download = useCallback(
    async (entry: VaultEntry) => {
      if (!activeProvider) return;
      setDownloading(entry.id);
      try {
        await downloadToDisk(
          {
            url: vaultDownloadUrl(activeProvider.id, entry.id),
            name: entry.name,
            totalBytes: entry.sizeBytes ?? 0,
          },
          () => {
            /* Fortschritt zeigt der Browser selbst; hier genügt der Zustand. */
          },
        );
      } catch (caught) {
        // Ein abgebrochener Speichern-Dialog ist eine Entscheidung, kein Fehler.
        if (!(caught instanceof DOMException && caught.name === "AbortError")) {
          toast.show(
            caught instanceof Error ? caught.message : "Der Download ist fehlgeschlagen.",
            "error",
          );
        }
      } finally {
        setDownloading(null);
      }
    },
    [activeProvider, toast],
  );

  // --- Hochladen -------------------------------------------------------------

  const upload = useCallback(
    (files: FileList | null) => {
      if (!activeProvider || !files || files.length === 0) return;

      for (const file of Array.from(files)) {
        const key = `${Date.now()}-${file.name}-${Math.random().toString(36).slice(2)}`;
        setUploads((current) => [
          ...current,
          { key, name: file.name, size: file.size, local: null, server: null },
        ]);

        const backend = vaultUploadBackend({
          providerId: activeProvider.id,
          folderId,
          name: file.name,
          size: file.size,
        });

        const uploader = new ChunkUploader(key, file, backend, (progress) => {
          setUploads((current) =>
            current.map((entry) => (entry.key === key ? { ...entry, local: progress } : entry)),
          );
        });

        void (async () => {
          await uploader.run();

          // `run()` wirft nicht, sondern meldet eine Störung über den
          // Fortschritt. Ohne diese Abfrage liefe der Statusabruf unten
          // trotzdem los, fände ewig "staging" vor und die Anzeige bliebe
          // ohne jede Meldung stehen -- der Fehler wäre unsichtbar.
          if (uploader.state === "error") return;

          // Nach der Übertragung folgt serverseitig noch Prüfsumme, Freigabe
          // und das Schreiben zum Ziel. Bis dahin wird der Stand abgefragt --
          // der Vault hat bewusst keine WebSocket-Verbindung.
          try {
            const ticket = await backend.createTicket();
            let versuch = 0;
            for (; versuch < 600; versuch += 1) {
              const status = await vaultApi.uploadStatus(ticket.uploadId);
              setUploads((current) =>
                current.map((entry) =>
                  entry.key === key ? { ...entry, server: status } : entry,
                ),
              );
              if (["done", "accepted", "failed", "denied", "expired"].includes(status.status)) {
                if (status.status === "done") {
                  toast.show(`${status.resultName ?? file.name} abgelegt.`, "success");
                  refresh();
                } else if (status.status === "accepted") {
                  // Bewusst kein "erfolgreich": die Datenquelle hat die Datei
                  // nur entgegengenommen.
                  toast.show(status.detail ?? "Von der Datenquelle angenommen.", "info");
                  refresh();
                } else {
                  toast.show(status.detail ?? "Der Upload ist fehlgeschlagen.", "error");
                }
                return;
              }
              await new Promise((resolve) => setTimeout(resolve, 500));
            }

            // Die Schleife ist durchgelaufen, ohne dass ein Endzustand kam.
            // Früher endete sie hier stillschweigend und die Anzeige blieb für
            // immer auf "Überträgt" stehen -- genau das darf nicht passieren.
            if (versuch >= 600) {
              const grenze = "Die Datenquelle antwortet seit fünf Minuten nicht. Der Upload läuft im Hintergrund weiter; lade die Ansicht später neu.";
              setUploads((current) =>
                current.map((entry) => (entry.key === key ? { ...entry, error: grenze } : entry)),
              );
              toast.show(grenze, "error");
            }
          } catch (caught) {
            setUploads((current) =>
              current.map((entry) =>
                entry.key === key
                  ? {
                      ...entry,
                      error:
                        caught instanceof ApiRequestError ? caught.message : "Upload fehlgeschlagen.",
                    }
                  : entry,
              ),
            );
          }
        })();
      }
    },
    [activeProvider, folderId, refresh, toast],
  );

  /**
   * Wiederholt das Schreiben zur Datenquelle.
   *
   * Danach wird derselbe Statusweg wie beim ersten Versuch abgefragt.
   */
  const retry = useCallback(
    (uploadId: string) => {
      void (async () => {
        try {
          await vaultApi.retryUpload(uploadId);
        } catch (caught) {
          toast.show(
            caught instanceof ApiRequestError ? caught.message : "Die Wiederholung schlug fehl.",
            "error",
          );
          return;
        }

        for (let versuch = 0; versuch < 600; versuch += 1) {
          const status = await vaultApi.uploadStatus(uploadId);
          setUploads((current) =>
            current.map((entry) =>
              entry.server?.uploadId === uploadId ? { ...entry, server: status, error: undefined } : entry,
            ),
          );
          if (["done", "accepted", "failed", "denied", "expired"].includes(status.status)) {
            if (status.status === "done") {
              toast.show(`${status.resultName ?? status.name} abgelegt.`, "success");
              refresh();
            } else if (status.status === "accepted") {
              toast.show(status.detail ?? "Von der Datenquelle angenommen.", "info");
              refresh();
            } else {
              toast.show(status.detail ?? "Der Upload ist erneut fehlgeschlagen.", "error");
            }
            return;
          }
          await new Promise((resolve) => setTimeout(resolve, 500));
        }
      })();
    },
    [refresh, toast],
  );

  const createFolder = useCallback(async () => {
    if (!activeProvider) return;
    const name = window.prompt("Name des neuen Ordners:");
    if (!name) return;

    try {
      await vaultApi.createFolder(activeProvider.id, folderId, name);
      toast.show(`Ordner „${name}" angelegt.`, "success");
      refresh();
    } catch (caught) {
      toast.show(
        caught instanceof ApiRequestError ? caught.message : "Der Ordner ließ sich nicht anlegen.",
        "error",
      );
    }
  }, [activeProvider, folderId, refresh, toast]);

  // --- Darstellung -----------------------------------------------------------

  if (!activeProvider) {
    return (
      <p style={{ color: "var(--color-granite)" }}>
        {error ?? "Lädt…"}
      </p>
    );
  }

  const caps = activeProvider.capabilities;

  return (
    <div>
      {/*
        Eine vorübergehend nicht erreichbare Nextcloud ist kein Fehler der
        Anwendung. Sie wird als Zustand gezeigt, statt bei jedem Klick eine
        Fehlermeldung zu produzieren.
      */}
      {!activeProvider.reachable ? (
        <div
          role="status"
          style={{
            marginBottom: "var(--space-3)",
            padding: "var(--space-2) var(--space-3)",
            border: "1.5px solid var(--color-danger)",
            borderRadius: "var(--radius-consent)",
            color: "var(--color-danger)",
          }}
        >
          <strong>{activeProvider.label} ist gerade nicht erreichbar.</strong>
          {activeProvider.detail ? ` ${activeProvider.detail}` : ""}
        </div>
      ) : null}
      {providers.length > 1 ? (
        <div style={{ display: "flex", gap: "var(--space-1)", marginBottom: "var(--space-4)" }}>
          {providers.map((provider) => (
            <Button
              key={provider.id}
              variant={provider.id === activeProvider.id ? "primary" : "secondary"}
              onClick={() => setActiveProvider(provider)}
            >
              {provider.label}
              {provider.reachable ? "" : " · gestört"}
            </Button>
          ))}
        </div>
      ) : null}

      {caps.supportsSearch ? (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void runSearch();
          }}
          style={{ display: "flex", gap: "var(--space-1)", marginBottom: "var(--space-3)" }}
        >
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Suchen…"
            aria-label="In dieser Datenquelle suchen"
            style={{
              flex: 1,
              minHeight: 44,
              padding: "0 var(--space-3)",
              background: "var(--color-white)",
              border: "1.5px solid var(--color-ink)",
              borderRadius: "var(--radius-pill)",
            }}
          />
          <Button type="submit" disabled={query.trim().length === 0}>
            Suchen
          </Button>
          {searching ? (
            <Button
              variant="secondary"
              onClick={() => {
                setQuery("");
                void loadFolder(activeProvider, folderId);
              }}
            >
              Zurück
            </Button>
          ) : null}
        </form>
      ) : null}

      {/* Navigation und Aktionen */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "var(--space-2)",
          flexWrap: "wrap",
          marginBottom: "var(--space-3)",
        }}
      >
        {searching ? (
          <Status kind="waiting">Suchergebnisse für „{query}"</Status>
        ) : (
          <>
            {parentId !== null ? (
              <Button variant="ghost" onClick={() => void loadFolder(activeProvider, parentId)}>
                ← Eine Ebene höher
              </Button>
            ) : null}
            <span style={{ fontSize: "var(--text-small)", color: "var(--color-granite)" }}>
              {/*
                Quellen ohne Ordner -- etwa Paperless -- zeigen die zuletzt
                hinzugefügten Dokumente. Das ist keine "oberste Ebene".
              */}
              {!caps.supportsFolders
                ? "Zuletzt hinzugefügt"
                : folderId === ""
                  ? "Oberste Ebene"
                  : folderId}
            </span>
          </>
        )}

        <span style={{ flex: 1 }} />

        {caps.supportsCreateFolder && !searching ? (
          <Button variant="secondary" onClick={() => void createFolder()}>
            Ordner anlegen
          </Button>
        ) : null}
      </div>

      {truncated ? (
        <p
          role="status"
          style={{
            marginBottom: "var(--space-2)",
            fontSize: "var(--text-small)",
            color: "var(--color-danger)",
          }}
        >
          Es gibt mehr Treffer, als angezeigt werden. Bitte den Suchbegriff verfeinern.
        </p>
      ) : null}

      {/* Ablagefläche für Uploads */}
      {caps.supportsUpload && !searching ? (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          onDragOver={(event) => {
            event.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(event) => {
            event.preventDefault();
            setDragging(false);
            upload(event.dataTransfer.files);
          }}
          style={{
            width: "100%",
            padding: "var(--space-3)",
            marginBottom: "var(--space-3)",
            background: dragging ? "var(--color-lifted)" : "transparent",
            border: `1.5px dashed ${dragging ? "var(--color-ink)" : "var(--color-dust)"}`,
            borderRadius: "var(--radius-consent)",
            color: "var(--color-granite)",
            cursor: "pointer",
          }}
        >
          <Icons.Upload size={20} />
          <span style={{ marginLeft: "var(--space-1)" }}>
            Dateien hierher ziehen oder auswählen
          </span>
        </button>
      ) : null}

      <input
        ref={inputRef}
        type="file"
        multiple
        className="visually-hidden"
        onChange={(event) => {
          upload(event.target.files);
          event.target.value = "";
        }}
      />

      {uploads.length > 0 ? <UploadList uploads={uploads} onRetry={retry} /> : null}

      {error ? (
        <p role="alert" style={{ color: "var(--color-danger)" }}>
          {error}
        </p>
      ) : loading ? (
        <p style={{ color: "var(--color-granite)" }}>Lädt…</p>
      ) : entries.length === 0 ? (
        <p style={{ color: "var(--color-granite)" }}>
          {searching ? "Keine Treffer." : "Dieser Ordner ist leer."}
        </p>
      ) : (
        <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: "var(--space-1)" }}>
          {entries.map((entry) => (
            <li key={entry.id}>
              <EntryRow
                entry={entry}
                downloading={downloading === entry.id}
                onOpen={() => void loadFolder(activeProvider, entry.id)}
                onDownload={() => void download(entry)}
                canDownload={caps.supportsDownload}
                originalOnly={caps.supportsOriginalDownloadOnly}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function EntryRow({
  entry,
  downloading,
  onOpen,
  onDownload,
  canDownload,
  originalOnly,
}: {
  entry: VaultEntry;
  downloading: boolean;
  onOpen: () => void;
  onDownload: () => void;
  canDownload: boolean;
  /** Die Quelle liefert nur die Originalfassung -- das gehört in die Beschriftung. */
  originalOnly: boolean;
}): React.ReactElement {
  const isFolder = entry.kind === "folder";

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "var(--space-2)",
        padding: "var(--space-1) var(--space-3)",
        background: "var(--color-canvas)",
        borderRadius: "var(--radius-consent)",
        minHeight: 56,
      }}
    >
      <span aria-hidden="true" style={{ color: "var(--color-granite)" }}>
        {isFolder ? "📁" : "📄"}
      </span>

      {isFolder ? (
        <button
          type="button"
          onClick={onOpen}
          style={{
            flex: 1,
            textAlign: "left",
            background: "none",
            border: "none",
            padding: 0,
            cursor: "pointer",
            fontWeight: "var(--weight-display)",
            wordBreak: "break-all",
          }}
        >
          {entry.name}
        </button>
      ) : (
        <span style={{ flex: 1, wordBreak: "break-all" }}>{entry.name}</span>
      )}

      <span
        style={{
          fontSize: "var(--text-small)",
          color: "var(--color-granite)",
          whiteSpace: "nowrap",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {entry.sizeBytes === null ? "" : formatBytes(entry.sizeBytes)}
      </span>

      {!isFolder && canDownload ? (
        <Button variant="secondary" onClick={onDownload} disabled={downloading}>
          {downloading ? "Speichert…" : originalOnly ? "Original speichern" : "Speichern"}
        </Button>
      ) : null}
    </div>
  );
}

/**
 * Zeigt laufende Uploads.
 *
 * Bewusst zweigeteilt: erst die Übertragung zum Server, danach Prüfsumme,
 * Freigabe und Ablage. Nur so ist erkennbar, woran es hängt, wenn ein Upload
 * bei „Wartet auf Freigabe" stehen bleibt.
 */
function UploadList({
  uploads,
  onRetry,
}: {
  uploads: ActiveUpload[];
  onRetry: (uploadId: string) => void;
}): React.ReactElement {
  return (
    <section style={{ marginBottom: "var(--space-4)" }}>
      <h3
        style={{
          fontSize: "var(--text-small)",
          fontWeight: "var(--weight-bold)",
          letterSpacing: "var(--tracking-eyebrow)",
          textTransform: "uppercase",
          color: "var(--color-granite)",
        }}
      >
        Uploads
      </h3>

      <ul style={{ listStyle: "none", margin: "var(--space-2) 0 0", padding: 0, display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
        {uploads.map((upload) => {
          const status = upload.server?.status ?? "staging";
          const uebertragen = upload.local?.sentBytes ?? 0;
          // Scheitert schon die Strecke zum Server, gibt es serverseitig gar
          // keinen Zustand -- ohne diese Zeile bliebe die Anzeige auf
          // "Überträgt" stehen und der Balken auf der Stelle.
          const uebertragungGescheitert = upload.local?.state === "error";
          const laeuftUebertragung = status === "staging" && !uebertragungGescheitert;
          const meldung = upload.error ?? upload.local?.error ?? upload.server?.detail ?? null;

          return (
            <li
              key={upload.key}
              style={{
                padding: "var(--space-2) var(--space-3)",
                background: "var(--color-canvas)",
                borderRadius: "var(--radius-consent)",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", gap: "var(--space-2)" }}>
                <span style={{ fontWeight: "var(--weight-display)", wordBreak: "break-all" }}>
                  {upload.server?.resultName ?? upload.name}
                </span>
                <span style={{ fontSize: "var(--text-small)", color: "var(--color-granite)", whiteSpace: "nowrap" }}>
                  {formatBytes(upload.size)}
                </span>
              </div>

              <div
                style={{
                  marginTop: 4,
                  fontSize: "var(--text-small)",
                  color:
                    status === "done"
                      ? "var(--color-success)"
                      : uebertragungGescheitert || ["failed", "denied", "expired"].includes(status)
                        ? "var(--color-danger)"
                        // "angenommen" ist bewusst neutral eingefärbt: grün
                        // würde einen Abschluss suggerieren, den es nicht gibt.
                        : "var(--color-granite)",
                }}
              >
                {uebertragungGescheitert ? "Übertragung fehlgeschlagen" : UPLOAD_STATUS_TEXT[status]}
                {upload.server?.resultName && upload.server.resultName !== upload.name
                  ? ` · umbenannt zu „${upload.server.resultName}"`
                  : ""}
              </div>

              {laeuftUebertragung && upload.size > 0 ? (
                <div style={{ marginTop: "var(--space-2)" }}>
                  <Progress
                    value={uebertragen / upload.size}
                    label="Überträgt"
                    detail={`${formatBytes(uebertragen)} von ${formatBytes(upload.size)}`}
                  />
                </div>
              ) : null}

              {/*
                Der Zusatztext ist nicht immer ein Fehler: bei "angenommen"
                trägt er die Auftragskennung. Rot einzufärben würde eine
                Störung suggerieren, wo keine ist -- und `role="alert"` würde
                einen Screenreader unnötig unterbrechen.
              */}
              {meldung
                ? (() => {
                    const istFehler =
                      Boolean(upload.error) ||
                      uebertragungGescheitert ||
                      ["failed", "denied", "expired"].includes(status);
                    return (
                      <p
                        {...(istFehler ? { role: "alert" as const } : {})}
                        style={{
                          marginTop: "var(--space-1)",
                          fontSize: "var(--text-small)",
                          color: istFehler ? "var(--color-danger)" : "var(--color-granite)",
                        }}
                      >
                        {meldung}
                      </p>
                    );
                  })()
                : null}

              {/*
                Die Datei liegt noch vollständig im Zwischenspeicher. Ein
                zweiter Versuch spart den gesamten Upload — bei mehreren
                Gigabyte der Unterschied zwischen einem Klick und einer halben
                Stunde.
              */}
              {upload.server?.retryable && upload.server.uploadId ? (
                <div style={{ marginTop: "var(--space-2)" }}>
                  <Button variant="secondary" onClick={() => onRetry(upload.server!.uploadId)}>
                    Erneut versuchen
                  </Button>
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
